import { BankrateOffer } from '@bankrate/mortgage-types'
import { filterOffersByDisplay } from 'store/models/offers'
import store from 'store'
import { RecursivePartial } from 'types/utils'
import currency from './currency'

export interface BankrateDisplayOffer extends BankrateOffer {
  rowType?: 'primary' | 'extra' | 'message'
  locationOverride?: 'rate-table' | 'additional offers' | 'featured tab' | 'rate-table-stacked' | 'rate-table-all'
}

export const getFeeAmount = (offer: BankrateOffer, HUDLine: number): string => {
  const fee = offer.fees.find(fee => fee.HUDLine === HUDLine)

  return fee ? currency.format(fee.amount, 0) : '(unknown)'
}

export const getFeeDescription = (offer: BankrateOffer, HUDLine: number, fallback: string = '') => {
  const fee = offer.fees.find(fee => fee.HUDLine === HUDLine)

  return fee ? fee.description : fallback
}

export const getLicenses = (offer: BankrateOffer): string => {
  const info = []

  if (offer.advertiser.nmlsLicense) {
    info.push(`NMLS #${offer.advertiser.nmlsLicense}`)
  }
  if (offer.advertiser.stateLicense) {
    info.push(`State Lic: ${offer.advertiser.stateLicense}`)
  }
  return info.length ? info.join(' | ') : ''
}

export const getPointsFeeAmount = (offer: BankrateOffer): string => {
  const fee = offer.fees.find(fee => fee.HUDLine === 841)

  return currency.format(fee ? fee.amount : 0, 0)
}

export const getThirdPartyFees = (offer: BankrateOffer) => {
  const thirdPartyFees = offer.fees
    .filter(fee => !fee.required)
    .map(fee => {
      const label = fee.description ? fee.description : ''
      const value = isNaN(fee.amount) ? 0 : fee.amount

      return { label, value }
    })

  return thirdPartyFees
}

export const getReviewsLink = (offer: BankrateOffer) => {
  // globalThis.useSEOName is a feature flag from fantasia
  const seoName = ((globalThis.useSEOName ? offer.advertiser.seoName : offer.advertiser.name) || '0').replace(/\./g, '')

  return 'https://www.bankrate.com/mortgage/lenders/' + encodeURIComponent(seoName) + '/' + offer.advertiser.id + '/'
}

interface OffersStats {
  total: number
  standard: number
  featured: number
  editorial: number
  sponsored: number
}

type PartialOffer = RecursivePartial<BankrateOffer>

export const getOffersCounts = (offers: PartialOffer[]): OffersStats => {
  const displayTargets = store.getState().dimensions.displayTargets
  const featuredOffers = filterOffersByDisplay(offers as BankrateOffer[], 'featured', displayTargets)
  return {
    total: offers.length,
    featured: featuredOffers.length,
    editorial: offers.filter(o => o.type === 'editorial').length,
    standard: offers.filter(o => !o.isFeatured && o.isPaid).length,
    sponsored: offers.filter(o => o.type === 'sponsored').length,
  }
}

export const getNullOffer = (type: string) =>
  ({
    type: type,
    id: type,
    isPaid: false,
    isSponsored: false,
    advertiser: {
      name: '',
      reviews: {
        count: 0,
      },
    },
    product: {
      type: '',
    },
    rowType: 'message',
  } as unknown as BankrateDisplayOffer)

export const isSponsored = (offer: PartialOffer): boolean => offer.type === 'sponsored'
export const isEditorial = (offer: PartialOffer): boolean => offer.type === 'editorial'
export const isMessage = (offer: RecursivePartial<BankrateDisplayOffer>): boolean => offer.rowType === 'message'
export const isPrimary = (offer: RecursivePartial<BankrateDisplayOffer>): boolean => offer.rowType === 'primary'
export const isExtra = (offer: RecursivePartial<BankrateDisplayOffer>): boolean => offer.rowType === 'extra'

export const hasAllRequiredFees = (offer: BankrateOffer): boolean => {
  if (offer.fees.filter(fee => fee.required === true).length >= 3) {
    return true
  } else {
    return false
  }
}

export const sageSituationalSponsoredOffer = {
  id: 'sage-situational-sponsored-offer',
  type: 'sponsored',
  creative: {
    description: `<ul><li>Mortgages are confusing. Call an expert or just lock in your rate at 
      <span class="font-black" style="color: black">sagemortgage.com</span></li>
      <li>Sage has helped over 5,000 borrowers refinance $2 billion+ in loans in the last year.</li>
      <li>Home values are rising but so are rates. Lock in now for 30 years. It is your equity. Use it.</li></ul>`,
  },
  adSetId: '35253',
  advertiser: {
    id: 10689,
    reviews: {
      count: 260,
      averageRating: 4.8,
    },
    nmlsLicense: '1374724',
    phone: '(844) 262-5188',
  },
  tracking: {
    link:
      'https://ep.bankrate.com/hlink_redirects/brm_cpc.aspx?' +
      'e=DG%2Bz7T9Zk6qBMpuG7tFWJa38l90FfygEwj1wZbxzWUfa6Km%2B1ggo4EDM6KesC5mK%2BnwK0wM8hZ5AuThdo47VmAZ443lA70ujkF1fGaZRQZ%2BbjYYfPmzGj9C%2FPhwRk%2BKMsUzRhExHo2q3WCYzy7BBcI41RTzV2qqXleA%2F%2FiwJTfdGKHqweSKDVvqUBKF7gnpd0%2B4tDG4w68H6LCFAO2o6sUsioIE2NGjcFdly%2B2j6QKpEnYsdnzvEPoUG23O5QDEVTIiPB88vTuMJSlQ99pOOFGxrRz%2F5ujcdX2378LS%2BX90Ipubr1Q7MA8tix%2FndCE2ygV%2F11IC%2FUQAd%2F782L8EbaZTN%2B2Db4qk5f0zT%2BXL7673rSn12X%2FTTrnpjca4ptEtafC84Sou6upVLN1Oe%2FbDUrpt%2F81ZbJab8YakPO2yf%2Ffe4wiM3DUHjZr8WlNgm2mqJCmGmrxIcC4RfJQfUKoxiC4dT%2FeMbfS3JXSbem3VeWv9apBPYSry6uBXrxDyy2MMMauZPU6W%2FouVPYJNEff02YqbExxciYjH%2FbFxF9dGFeIvIQwyWxd7wn1zFsRjLPWTnNTK02mIFhbdB%2BdVrXGKk%2BSM8nR6ZX1sjkui4nmNk490GdtC%2Bp4tdyStBCjoONsR2duC8C120lTyE7O6hzBj%2BtHthU2Gkdah3ZtiSkCyTZp0mOuHYE8vDOw%3D%3D' +
      '&reqid=10000000-1000-4000-8000-100000036578&lead=1&adsetid=35253&adsettype=rate_listing&cs=0&pc=2&c=1%2C11%2C0%2C%2C%2Cbr3&iscpl=true&points=&directLink=true' +
      '&ref_url=https%3A%2F%2Fwww.bankrate.com%2Fmortgages%2Frefinance-rates%2F%3FmortgageType%3DRefinance%26partnerId%3Dbr3%26pid%3Dbr3%26pointsChanged%3Dfalse%26refinanceCashOutAmount%3D0%26refinanceLoanAmount%3D320000%26refinanceLoanTerms%3D30yr%26refinancePoints%3DAll%26refinancePropertyType%3DSingleFamily%26refinancePropertyUse%3DPrimaryResidence%26refinancePropertyValue%3D400000%26searchChanged%3Dfalse%26showingStacked%3Dall%26ttcid%26userCreditScore%3D780%26userFha%3Dfalse%26userVeteranStatus%3DNoMilitaryService%26zipCode%3D33410' +
      '&rj=eyJhbGciOiJIUzI1NiIsImNvbXByZXNzaW9uIjoiZ3ppcCJ9.SDRzSUFBQUFBQUFBQTBWUlRXK0RNQXo5SzFXazlVVDVDRkFvVWpSdGg1MDJxYmNkVVNDbWpRb2hja0pWTk8yL0x3bWI1b1A5bmwvc3lQWVg0YUkxWUZzcFNKT1h0TXdqd2pXU3BveElQOHIrMXRwVlExQ3JPaUlDakpXS1d6bXJkc0dSTk9ScXJUWk5rc3pEQUdqaWpxc2JjZ3R4UDAvSk5LTzk4QXVZcEo4NzVFb2t6eDIyV2pLYUhmY2VLWmFuVHpSZDBibEJQa0M0aURCSUwwNlNuZW9BRlB0MDN6cnB6TWZKaFZmZy9kVXJocjI5KzRpc2pOTTA5WkQvUTIwTmV4bkhrSjBzeTJuNks0Qm1tWVB4eHN4OVpUUS9WYVVubzcyek9xUUgyYytzS3RLOWxvSjFtSk9Jd01NQ0tqNkdiUkRmd3R2QmcwUGhYZjFIbmVYSHNxcGRrVFJ0cjkyaUxDNFFFYzNSS3NDdHc5WlZ6MUpaNCtpaWhCdGRnZkJKbk1YU2IxZHh5NHFJMzJrNENvSjdocTBJbkdRMHlVNEpwYnVNTm1YUkZNWHUvT0hLellKM1dGdXAzTFhzRXE0Vk92a3h2MzhBN1BBcUMvTUJBQUE9.U5r1G3-OGlGgjU78WyFqG6rB8KlUjQ_tbTC5F4gSrKc',
  },
  product: {},
} as unknown as BankrateOffer

export const offerClickDdTags = (offer: BankrateOffer, rowPosition: number) => {
  let tags = [
    `isFeatured:${offer.isFeatured}`,
    `size:${offer.product.size}`,
    `isFHA:${offer.product.isFHA}`,
    `isVA:${offer.product.isVA}`,
    `name:${offer.product.name}`,
    `rowPosition:${rowPosition}`,
    `advertiserId:${offer.advertiser.id}`,
  ]
  offer.displayTargets?.forEach(dt => tags.push(`displayTarget:${dt}`))
  return tags
}
